// dependencies
import type { SortChangedEvent } from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import { format } from "date-fns";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { faExclamationCircle, faPlus, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
	AvatarProfile,
	ButtonStandard,
	InputText,
	ModalBase,
	Table,
	Tabs,
	Text,
	Tooltip,
	toast,
} from "@performyard/py-components";
import type { PYColumnDef } from "@performyard/py-components";

//components
import { AdminTitle } from "../../../../../components/Admin";
import OptionsDrawer from "../../../../../components/OptionsDrawer";
import PageTitle from "../../../../../components/PageTitle";

// api
import {
	useArchiveCohort,
	useArchivedCohorts,
	useCohorts,
	useDeleteCohort,
	useReactivateCohort,
} from "../../../../../api/hooks";
import useNavigate from "../../../../../hooks/useNavigate";

// styles
import css from "./Cohorts.module.scss";
import { getSortedCohorts } from "./cohortsListUtils";

/* Types */
type CohortData = {
	_id: string;
	name: string;
	color: string;
	type: "static" | "dynamic";
	createdAt: Date | string;
	updatedAt: Date | string;
	memberCount: number;
	calculation_status: "idle" | "backpropagating" | "processing";
	archivedAt?: Date | string;
	archivedBy?: { _id: string; name: string };
};

/* Constants */

const description = "Customize and adjust settings to only display information that is relevant to you and your team.";
const archivedDescription =
	"Manage your archived cohorts by deleting them or reactivating them to display on the engagment dashboard.";

const activeCohortOptions = [
	{ id: "edit", label: "Edit" },
	{ id: "duplicate", label: "Duplicate" },
	{ id: "archive", label: "Archive" },
];

const archivedCohortOptions = [
	{ id: "reactivate", label: "Reactivate" },
	{ id: "delete", label: "Delete" },
];

/**
 * Contains the table column definitions as well as the controls that exist in
 * the options drawer.
 */
const useTableColumnsAndOptionsControls = (props: {
	maximumAllowedCohorts: number;
	maxActiveCohortsReached?: boolean;
	maxArchivedCohortsReached?: boolean;
	activeTab?: "active" | "archived";
	setMaxCohortReachedModal: (_value: boolean) => void;
	refetch: () => void;
}) => {
	const {
		maxActiveCohortsReached = false,
		maxArchivedCohortsReached = false,
		maximumAllowedCohorts,
		activeTab = "active",
		setMaxCohortReachedModal = () => {},
		refetch = () => {},
	} = props;
	const navigate = useNavigate();
	const [modal, setModal] = useState<null | { id: string; name: string; type: string }>(null);
	const { mutate: archiveCohort } = useArchiveCohort({
		onSuccess: () => {
			toast.success("Cohort Archived");
			refetch();
		},
		onError: (error) => {
			toast.error((error as Error).message);
		},
	});
	const { mutate: reactivateCohort } = useReactivateCohort({
		onSuccess: () => {
			toast.success("Cohort Reactivated");
			refetch();
		},
		onError: (error) => {
			toast.error((error as Error).message);
		},
	});
	const { mutate: deleteCohort } = useDeleteCohort({
		onSuccess: () => {
			toast.success("Cohort Deleted");
			refetch();
		},
		onError: (error) => {
			toast.error((error as Error).message);
		},
	});

	const handleDuplicate = async (cohortID: string) => {
		if (maxActiveCohortsReached && activeTab === "active") {
			setMaxCohortReachedModal(true);
		} else if (maxArchivedCohortsReached && activeTab === "archived") {
			setMaxCohortReachedModal(true);
		} else {
			try {
				navigate(`/administration/engagement/duplicate-cohort/${cohortID}`);
			} catch (error) {
				toast.error((error as Error).message);
			}
		}
	};

	const handleReactivateCohort = async (cohortID: string) => {
		if (!cohortID) return;
		if (maxActiveCohortsReached) {
			setMaxCohortReachedModal(true);
			return;
		}
		reactivateCohort({ cohortID });
		setModal(null);
	};

	const handleDelete = async () => {
		if (!modal) return;
		deleteCohort({ cohortID: modal.id });
		setModal(null);
	};

	const handleArchive = async () => {
		if (!modal) return;
		if (maxArchivedCohortsReached) {
			setModal({ id: modal.id, name: modal.name, type: "archiveFull" });
		}
		archiveCohort({ cohortID: modal.id });
		setModal(null);
	};

	const closeButton = (
		<div>
			<ButtonStandard size={"large"} color="primary" onClick={() => setModal(null)}>
				Close
			</ButtonStandard>
		</div>
	);

	const deleteButton = (
		<div>
			<ButtonStandard size={"large"} color="red" onClick={handleDelete}>
				Yes, Delete Cohort
			</ButtonStandard>
		</div>
	);

	const cancelButton = (
		<div>
			<ButtonStandard size={"large"} color="primary" onClick={() => setModal(null)}>
				Cancel
			</ButtonStandard>
		</div>
	);

	const archiveButton = (
		<div>
			<ButtonStandard size={"large"} color="green" onClick={handleArchive}>
				Yes, Archive Cohort
			</ButtonStandard>
		</div>
	);

	let updateModal: React.ReactElement | null = null;

	const modalContent = (modal: any, type: string) => {
		if (type === "archiveFull") {
			return (
				<Text
					overrideElement="span"
					size="paragraph-2"
					weight="regular"
					dangerouslySetClassName={css.deleteModal}
				>
					You have reached the maximum {maximumAllowedCohorts} Cohort archive limit. Please delete archived
					cohorts to archive new cohorts
				</Text>
			);
		} else {
			return (
				<div>
					Are you sure you want to {type} the cohort &quot;
					<Text
						overrideElement="span"
						size="paragraph-2"
						weight="regular"
						dangerouslySetClassName={css.deleteModal}
					>
						{modal.name}
					</Text>
					&quot;?
				</div>
			);
		}
	};

	if (modal) {
		if (modal.type === "delete") {
			updateModal = (
				<ModalBase isOpen={true} onClose={() => setModal(null)}>
					<div className="w-96 px-6 pb-6">
						<div className="text-center">
							<Text
								overrideElement="span"
								size="sub-heading"
								weight="semi-bold"
								dangerouslySetClassName={css.deleteModal}
							>
								Delete Cohort?
							</Text>
						</div>
						<div className="text-center">{modalContent(modal, "delete")}</div>
						<div className="flex justify-center gap-2 pt-4">
							{cancelButton}
							{deleteButton}
						</div>
					</div>
				</ModalBase>
			);
		} else if (modal.type === "archive") {
			if (!maxArchivedCohortsReached) {
				updateModal = (
					<ModalBase isOpen={true} onClose={() => setModal(null)}>
						<div className="w-96 px-6 pb-6">
							<div className="text-center">
								<Text
									overrideElement="span"
									size="sub-heading"
									weight="semi-bold"
									dangerouslySetClassName={css.deleteModal}
								>
									Archive Cohort?
								</Text>
							</div>
							<div className="text-center">{modalContent(modal, "archive")}</div>
							<div className="flex justify-center gap-2 pt-4">
								{cancelButton}
								{archiveButton}
							</div>
						</div>
					</ModalBase>
				);
			} else {
				updateModal = (
					<ModalBase isOpen={true} onClose={() => setModal(null)}>
						<div className="w-96 px-6 pb-6">
							<div className="text-center">
								<Text
									overrideElement="span"
									size="sub-heading"
									weight="semi-bold"
									dangerouslySetClassName={css.deleteModal}
								>
									Archive Full
								</Text>
							</div>
							<div className="text-center">{modalContent(modal, "archiveFull")}</div>
							<div className="flex justify-center pt-4">{closeButton}</div>
						</div>
					</ModalBase>
				);
			}
		}
	}
	const baseTableCols: PYColumnDef<CohortData>[] = [
		{
			colId: "name",
			headerName: "Cohort",
			flex: 1.5,
			sortable: true,
			cellRenderer: (params: { data: CohortData }) => {
				const row = params.data;
				if (!row || !row.name) return null;
				return (
					<div className={css.nameCell}>
						<div className={css.cohortColor} style={{ backgroundColor: row.color }} />
						<Text size="paragraph-3" weight="regular">
							<span className="py-line-clamp-1 max-w-[250px]">{row.name}</span>
						</Text>
					</div>
				);
			},
		},
		{
			colId: "cohortType",
			headerName: "Cohort Type",
			flex: 1,
			sortable: true,
			cellRenderer: (params: { data: CohortData }) => {
				const row = params.data;
				if (!row || !row.type) return null;
				return row.type === "static" ? "Static" : "Dynamic";
			},
		},
		{
			colId: "createdAt",
			headerName: "Date Created",
			flex: 1,
			sortable: true,
			cellRenderer: (params: { data: CohortData }) => {
				const row = params.data;
				if (!row || !row.createdAt) return null;
				const createdDate = row.createdAt instanceof Date ? row.createdAt : new Date(row.createdAt);
				return format(createdDate, "MMM dd, yyyy");
			},
		},
	];

	const activeCohortTableCols: PYColumnDef<CohortData>[] = [
		{
			colId: "updatedAt",
			headerName: "Last Modified",
			flex: 1,
			sortable: true,
			cellRenderer: (params: { data: CohortData }) => {
				const row = params.data;
				if (!row || !row.updatedAt) return null;
				const updatedDate = row.updatedAt instanceof Date ? row.updatedAt : new Date(row.updatedAt);
				return format(updatedDate, "MMM dd, yyyy");
			},
		},
		{
			colId: "memberCount",
			headerName: "Cohort Size",
			flex: 1,
			sortable: true,
			cellRenderer: (params: { data: CohortData }) => {
				const row = params.data;
				if (!row || row.memberCount === undefined) return null;
				if (row.memberCount < 3) {
					return (
						<span className={css.cohortSizeCell}>
							{row.memberCount}
							<Tooltip
								content={
									"Analytics dashboard charts will only display results for a cohort if a sufficient number of cohort members (3) have responded to surveys."
								}
							>
								<FontAwesomeIcon
									icon={faExclamationCircle}
									data-testid="circle-exclamation"
									className={css.sizeWarningIcon}
								/>
							</Tooltip>
						</span>
					);
				}
				return row.memberCount;
			},
		},
		{
			colId: "status",
			headerName: "Status",
			flex: 1,
			sortable: true,
			cellRenderer: (params: { data: CohortData }) => {
				const row = params.data;
				if (!row || !row.calculation_status) return null;
				const labels = {
					idle: {
						label: "Active",
						color: "green",
					},
					backpropagating: {
						label: "Processing",
						color: "grey",
					},
					processing: {
						label: "Processing",
						color: "grey",
					},
				};

				const config = labels[row.calculation_status] ?? { label: "Active", color: "green" };

				return (
					<div className={`${css.tableChip} ${css[config.color]}`}>
						<Text size="paragraph-3" weight="semi-bold">
							{config.label}
						</Text>
					</div>
				);
			},
		},
		{
			colId: "options",
			headerName: "",
			sortable: false,
			width: 60,
			cellRenderer: (params: { data: CohortData }) => {
				const row = params.data;
				if (!row || !row._id) return null;
				const handleSelect = async (option: { id: string; label: string }) => {
					switch (option.id) {
						case "edit":
							navigate(`/administration/engagement/cohorts/${row._id}`);
							break;
						case "duplicate":
							handleDuplicate(row._id);
							break;
						case "delete":
							setModal({ id: row._id, name: row.name, type: "delete" });
							break;
						case "reactivate":
							handleReactivateCohort(row._id);
							break;
						case "archive":
							setModal({ id: row._id, name: row.name, type: "archive" });
							break;
					}
				};

				return (
					<div className={css.tableOptions}>
						<OptionsDrawer
							openOn="bottom"
							horizontal={true}
							onSelect={handleSelect}
							options={activeTab !== "active" ? archivedCohortOptions : activeCohortOptions}
						/>
					</div>
				);
			},
		},
	];

	const archivedCohortTableCols: PYColumnDef<CohortData>[] = [
		{
			colId: "archivedAt",
			headerName: "Date Archived",
			flex: 1,
			sortable: true,
			cellRenderer: (params: { data: CohortData }) => {
				const row = params.data;
				if (!row?.archivedAt) return "";
				const archivedDate = row.archivedAt instanceof Date ? row.archivedAt : new Date(row.archivedAt);
				return format(archivedDate, "MMM dd, yyyy");
			},
		},
		{
			colId: "archivedBy",
			headerName: "Archived By",
			flex: 1,
			sortable: true,
			cellRenderer: (params: { data: CohortData }) => {
				const row = params.data;
				if (!row || !row.archivedBy) return null;
				return <AvatarProfile employeeId={row.archivedBy._id} employeeName={row.archivedBy.name} />;
			},
		},
		{
			colId: "options",
			headerName: "",
			sortable: false,
			width: 60,
			cellRenderer: (params: { data: CohortData }) => {
				const row = params.data;
				if (!row || !row._id) return null;
				const handleSelect = async (option: { id: string; label: string }) => {
					switch (option.id) {
						case "edit":
							navigate(`/administration/engagement/cohorts/${row._id}`);
							break;
						case "duplicate": {
							handleDuplicate(row._id);
							break;
						}
						case "delete":
							setModal({ id: row._id, name: row.name, type: "delete" });
							break;
						case "reactivate":
							handleReactivateCohort(row._id);
							break;
						case "archive":
							setModal({ id: row._id, name: row.name, type: "archive" });
							break;
					}
				};

				return (
					<div className={css.tableOptions}>
						<OptionsDrawer
							openOn="bottom"
							horizontal={true}
							onSelect={handleSelect}
							options={activeTab !== "active" ? archivedCohortOptions : activeCohortOptions}
						/>
					</div>
				);
			},
		},
	];

	const tableCols = useMemo(() => {
		if (activeTab === "archived") {
			return [...baseTableCols, ...archivedCohortTableCols];
		} else {
			return [...baseTableCols, ...activeCohortTableCols];
		}
	}, [navigate, activeTab]);

	return {
		modal: updateModal,
		columns: tableCols,
	};
};

const CohortTab = () => {
	// state management
	const [search, setSearch] = useState<string>("");
	const [sort, setSort] = useState<{ column: string; direction: "asc" | "desc" }>({
		column: "name",
		direction: "asc",
	});
	const [maxActiveCohortsReachedModalOpen, setMaxCohortsReachedModalOpen] = useState<boolean>(false);

	// AG Grid refs
	const activeGridRef = useRef<AgGridReact<CohortData>>(null);
	const archivedGridRef = useRef<AgGridReact<CohortData>>(null);

	const getInitialTab = (): "active" | "archived" => {
		if (typeof window !== "undefined") {
			const params = new URLSearchParams(window.location.search);
			const tab = params.get("tab");
			if (tab === "active" || tab === "archived") {
				return tab;
			}
		}
		return "active";
	};

	const [activeTab, setActiveTab] = useState<"active" | "archived">(getInitialTab());

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const tab = params.get("tab");
		if (tab === "active" || tab === "archived") {
			setActiveTab(tab);
		}
	}, []);

	const handleTabChange = (value: string) => {
		if (value === "active" || value === "archived") {
			setActiveTab(value);
		}
	};

	const { data, isLoading, refetch } = useCohorts();
	const {
		data: archivedCohortData,
		isLoading: isLoadingArchivedCohortData,
		refetch: refetchArchivedCohorts,
	} = useArchivedCohorts();

	const {
		allCohorts,
		allArchivedCohorts,
		maximumAllowedCohorts,
		maxActiveCohortsReached,
		maxArchivedCohortsReached,
	} = useMemo(() => {
		const maximumAllowedCohorts = data?.maximumAllowedCohorts ?? 0;
		const currentActiveCohortCount = data?.currentCohortCount ?? 0;
		const currentArchivedCohortCount = archivedCohortData?.currentCohortCount ?? 0;

		return {
			allCohorts: data?.cohorts ?? [],
			allArchivedCohorts: archivedCohortData?.cohorts ?? [],
			maximumAllowedCohorts,
			maxActiveCohortsReached: currentActiveCohortCount >= maximumAllowedCohorts,
			maxArchivedCohortsReached: currentArchivedCohortCount >= maximumAllowedCohorts,
		};
	}, [data, archivedCohortData]);

	const refetchAllCohorts = async () => {
		await refetch();
		await refetchArchivedCohorts();
	};

	const { columns, modal: updateModal } = useTableColumnsAndOptionsControls({
		maxActiveCohortsReached,
		maxArchivedCohortsReached,
		maximumAllowedCohorts,
		activeTab,
		setMaxCohortReachedModal: (value) => setMaxCohortsReachedModalOpen(value),
		refetch: refetchAllCohorts,
	});

	const pendingCohorts = allCohorts.filter((cohort) => cohort.calculation_status !== "idle").length;
	const pendingArchivedCohorts = allArchivedCohorts.filter((cohort) => cohort.calculation_status !== "idle").length;

	const navigate = useNavigate();

	useEffect(() => {
		let refetchTimeout;
		if (pendingCohorts > 0) {
			refetchTimeout = setInterval(() => {
				refetch();
			}, 5000);
		}
		if (pendingArchivedCohorts > 0) {
			refetchTimeout = setInterval(() => {
				refetchArchivedCohorts();
			}, 5000);
		}
		return () => {
			clearInterval(refetchTimeout);
		};
	}, [pendingCohorts, pendingArchivedCohorts]);

	// search and sort cohorts
	const cohorts = useMemo(() => {
		const items = allCohorts;

		return getSortedCohorts(items, sort.column, sort.direction, search);
	}, [data, search, sort]);

	const archivedCohorts = useMemo(() => {
		const items = allArchivedCohorts;

		return getSortedCohorts(items, sort.column, sort.direction, search);
	}, [archivedCohortData, search, sort]);

	// Handle sort changes from AG Grid
	const handleSortChanged = useCallback((event: SortChangedEvent<CohortData>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn && sortedColumn.colId) {
			setSort({
				column: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	}, []);

	const closeButton = (
		<div>
			<ButtonStandard size={"large"} color="primary" onClick={() => setMaxCohortsReachedModalOpen(false)}>
				Close
			</ButtonStandard>
		</div>
	);

	const descriptionContent = (
		<div className={css.description}>
			<Text overrideElement="span" size="paragraph-3" weight="regular" color={"neutral-9"}>
				{activeTab === "archived" ? archivedDescription : description}
			</Text>
		</div>
	);

	const searchAndCreateCohortContent = (
		<div className={css.tableControls}>
			<InputText
				value={search}
				onChange={setSearch}
				placeholder="Search fields..."
				leftIcon={faSearch}
				clearable
				aria-label="Search fields"
			/>

			<div />

			<div>
				{activeTab !== "archived" && (
					<ButtonStandard
						onClick={
							!maxActiveCohortsReached
								? () => navigate("/administration/engagement/create-cohort")
								: () => setMaxCohortsReachedModalOpen(true)
						}
						color="primary"
						iconLeft={faPlus}
					>
						Create Cohort
					</ButtonStandard>
				)}
			</div>
		</div>
	);

	const activeCohortsContent = (
		<div>
			{descriptionContent}
			{updateModal}
			{searchAndCreateCohortContent}

			<div
				className={css.tableWrapper}
				style={{
					height: cohorts?.length ? `${44 + cohorts.length * 56}px` : "194px",
				}}
			>
				<Table<CohortData>
					ref={activeGridRef}
					columnDefs={columns}
					rowData={cohorts}
					loading={isLoading}
					getRowId={(params) => params.data._id}
					onSortChanged={handleSortChanged}
					rowHeight={56}
					defaultColDef={{
						cellStyle: { display: "flex", alignItems: "center" },
						headerStyle: { paddingLeft: "4px" },
						resizable: false,
					}}
					noRowsOverlayComponent={() => null}
				/>
				{!isLoading && cohorts?.length === 0 && (
					<div className={css.customOverlay}>
						{allCohorts.length === 0 ? (
							<>
								<Text size="paragraph-1" weight="semi-bold">
									<span className="mb-2 flex flex-col items-center">
										<span>You currently have no Employee Cohorts.</span>
										<span>Create a Cohort to begin.</span>
									</span>
								</Text>
								<ButtonStandard
									onClick={() => navigate("/administration/engagement/create-cohort")}
									color="primary"
									iconLeft={faPlus}
								>
									Create Cohort
								</ButtonStandard>
							</>
						) : (
							<Text size="paragraph-1" weight="semi-bold">
								No results found
							</Text>
						)}
					</div>
				)}
			</div>
		</div>
	);

	const archivedCohortsContent = (
		<div>
			{descriptionContent}
			{updateModal}
			{searchAndCreateCohortContent}

			<div
				className={css.tableWrapper}
				style={{
					height: archivedCohorts?.length ? `${44 + archivedCohorts.length * 56}px` : "194px",
				}}
			>
				<Table<CohortData>
					ref={archivedGridRef}
					columnDefs={columns}
					rowData={archivedCohorts}
					loading={isLoadingArchivedCohortData}
					getRowId={(params) => params.data._id}
					onSortChanged={handleSortChanged}
					rowHeight={56}
					defaultColDef={{
						cellStyle: { display: "flex", alignItems: "center" },
						headerStyle: { paddingLeft: "4px" },
						resizable: false,
					}}
					noRowsOverlayComponent={() => null}
				/>
				{!isLoadingArchivedCohortData && archivedCohorts?.length === 0 && (
					<div className={css.customOverlay}>
						<Text size="paragraph-1" weight="semi-bold">
							{allArchivedCohorts.length === 0
								? "You currently have no archived Employee Cohorts."
								: "No results found"}
						</Text>
					</div>
				)}
			</div>
		</div>
	);

	return (
		<>
			<PageTitle title="Employee Engagement Settings" />
			<div className={css.titleWrapper}>
				<AdminTitle includeSpacer={false}>Employee Cohorts</AdminTitle>
			</div>
			<div className={css.tabs}>
				<div>
					<Tabs
						tabs={[
							{
								value: "active",
								label: "Active",
								content: activeCohortsContent,
							},
							{
								value: "archived",
								label: "Archived",
								content: archivedCohortsContent,
							},
						]}
						value={activeTab}
						onValueChange={handleTabChange}
						urlParam="tab"
						color="neutral"
						size="large"
						data-testid="cohort-status-tab"
					></Tabs>
				</div>
			</div>
			<ModalBase isOpen={maxActiveCohortsReachedModalOpen} onClose={() => setMaxCohortsReachedModalOpen(false)}>
				<div className="w-96 px-6 pb-6">
					<div className="text-center">
						<Text
							overrideElement="span"
							size="sub-heading"
							weight="semi-bold"
							dangerouslySetClassName={css.deleteModal}
						>
							Cohorts Full
						</Text>
					</div>
					<div className="text-center">
						<Text
							overrideElement="span"
							size="paragraph-2"
							weight="regular"
							dangerouslySetClassName={css.deleteModal}
						>
							You have reached the maximum {maximumAllowedCohorts} Cohort limit. Consider archiving or
							deleting old cohorts to free up more space.
						</Text>
					</div>
					<div className="flex justify-center pt-4">{closeButton}</div>
				</div>
			</ModalBase>
		</>
	);
};

export default CohortTab;
